import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { ALCANCES, consultarPorcentajeAnual, generarPresupuestoDelAnio } from '../api/presupuestoApi.js';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import {
  avisoDeCategoriasSinPresupuesto, importe, listaDeMeses, nombreDePeriodo, porcentaje,
} from '../formato.js';

/**
 * ASISTENTE «DEFINIR EL PRESUPUESTO DE AAAA» (RF-110, RN-229, RN-233).
 *
 * Cuatro pasos: porcentaje → alcance (cuando aplica) → vista previa → confirmado.
 * NADA SE ESCRIBE HASTA CONFIRMAR, y lo que se muestra en la vista previa es
 * exactamente lo que se escribe (RNF-055).
 *
 * LAS REGLAS QUE MÁS CARO SALEN SI NO SE MIRAN, Y DÓNDE ESTÁN AQUÍ:
 *   · PASO 1 · Se muestra el PORCENTAJE VIGENTE REAL y la opción de no tocarlo se
 *     rotula con su número —«Mantener el +10 % ya fijado para 2026»—. «Dejar el
 *     año anterior tal cual» SOLO aparece si nadie lo ha fijado (RN-216 bis d).
 *     Omitir el campo NO borra el porcentaje: ausente significa "no lo cambio".
 *   · PASO 2 · DOS OPCIONES NOMBRADAS Y NINGUNA PRESELECCIONADA. No es un
 *     interruptor ni una casilla. Si no se elige, el servidor responde
 *     PARAMETRO_INVALIDO y se muestra: no se bloquea en silencio (RN-233c).
 *   · PASO 3 · El titular usa `cantidadDeCategoriasSinPresupuesto`, que manda EL
 *     SERVIDOR por alcance y en CATEGORÍAS DISTINTAS (D-167, RN-233 b ter): la
 *     pantalla lo pinta tal cual, no suma listas (RNF-053).
 *   · SUSTITUIR_TODO ES LA ÚNICA ACCIÓN DESTRUCTIVA: tratamiento visual propio,
 *     lista NOMINAL de lo que se pierde y CONFIRMACIÓN APARTE (RN-233e).
 *   · CADA CASO EN UN SOLO SITIO: lo manual solo en el bloque destructivo; lo que
 *     queda sin cifra pase lo que pase, solo en su lista (RN-233 b ter).
 */
const PASOS = { porcentaje: 1, alcance: 2, vistaPrevia: 3, hecho: 4 };
const MODOS = { mantener: 'MANTENER', cambiar: 'CAMBIAR' };

export default function AsistenteDelAnio() {
  const { anio: anioDeLaRuta } = useParams();
  const [parametros] = useSearchParams();
  const hoy = obtenerFechaDeNegocioHoy();
  const anio = Number(anioDeLaRuta);
  const periodoDeOrigen = Number(parametros.get('desde')) || null;

  const [vigente, setVigente] = useState(null);
  const [paso, setPaso] = useState(PASOS.porcentaje);
  const [modo, setModo] = useState(null);
  const [porcentajeTecleado, setPorcentajeTecleado] = useState('');
  const [confirmadoAlcanceAnual, setConfirmadoAlcanceAnual] = useState(false);
  const [alcance, setAlcance] = useState(null);
  const [confirmadoReemplazo, setConfirmadoReemplazo] = useState(false);
  const [previa, setPrevia] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [trabajando, setTrabajando] = useState(false);

  // El vigente se consulta para PODER ROTULAR la primera opción con su número
  // real. Consultar no fija nada y no crea ninguna fila (RN-215).
  useEffect(() => {
    let vigenteAun = true;
    consultarPorcentajeAnual(anio)
      .then((dato) => { if (vigenteAun) setVigente(dato); })
      .catch(() => { if (vigenteAun) setVigente(null); });
    return () => { vigenteAun = false; };
  }, [anio]);

  const estaFijado = vigente?.porcentajeAnualFijado === true;

  /** Cuerpo común de la petición. `porcentajeIncremento` SOLO viaja si se cambió. */
  function peticionBase() {
    const peticion = { fechaLocal: hoy };
    if (modo === MODOS.cambiar) {
      peticion.porcentajeIncremento = Number(String(porcentajeTecleado).replace('−', '-'));
      peticion.confirmadoAlcanceAnual = confirmadoAlcanceAnual;
    }
    return peticion;
  }

  async function pedirVistaPrevia() {
    setError(null);
    setTrabajando(true);
    try {
      // La vista previa NO ESCRIBE NADA y devuelve LOS DOS alcances (D-158).
      const respuesta = await generarPresupuestoDelAnio(anio, { ...peticionBase(), soloVistaPrevia: true });
      setPrevia(respuesta);

      // AÑO VACÍO (D-160, RN-233a bis): sin nada que conservar ni que sustituir en
      // NINGUNA de las dos ramas, los dos alcances son indistinguibles y el paso no
      // se pregunta. El parámetro se sigue enviando —es obligatorio siempre— y se
      // declara en pantalla; NO es un valor por defecto.
      if (esAnioVacio(respuesta)) {
        setAlcance(ALCANCES.conservarEditadoAMano);
        setPaso(PASOS.vistaPrevia);
      } else if (!respuesta.comparativoDeAlcances) {
        // Sin comparativo no hay cómo ofrecer la elección, y ELEGIR POR LA PERSONA
        // NO ES UNA OPCIÓN: se sigue sin alcance, el servidor responde
        // PARAMETRO_INVALIDO al confirmar y eso se muestra (RN-233c, D-158).
        setAlcance(null);
        setPaso(PASOS.vistaPrevia);
      } else {
        setAlcance(null);
        setPaso(PASOS.alcance);
      }
    } catch (fallo) {
      setError(fallo);
    } finally {
      setTrabajando(false);
    }
  }

  async function confirmarYGenerar() {
    setError(null);
    setTrabajando(true);
    try {
      const respuesta = await generarPresupuestoDelAnio(anio, {
        ...peticionBase(),
        soloVistaPrevia: false,
        confirmado: true,
        // SIN VALOR POR DEFECTO: si no se eligió, aquí viaja nulo y el servidor
        // responde PARAMETRO_INVALIDO, que se muestra tal cual (RN-233c).
        alcanceDeRegeneracion: alcance,
        confirmadoReemplazoDeCapturaManual: confirmadoReemplazo,
      });
      setResultado(respuesta);
      setPaso(PASOS.hecho);
    } catch (fallo) {
      setError(fallo);
    } finally {
      setTrabajando(false);
    }
  }

  const rama = previa?.comparativoDeAlcances
    ? (alcance === ALCANCES.sustituirTodo
      ? previa.comparativoDeAlcances.sustituirTodo
      : previa.comparativoDeAlcances.conservarEditadoAMano)
    : null;

  const manualesEnRiesgo = previa?.lineasManualesEnRiesgo ?? [];
  const esDestructivo = alcance === ALCANCES.sustituirTodo && manualesEnRiesgo.length > 0;

  return (
    <Shell titulo="Presupuesto" subtitulo={`Definir el presupuesto de ${anio}`}>
      <div className="card" style={{ margin: 12 }} data-prueba="pasos-del-asistente">
        <b>Definir el presupuesto de {anio}</b>
        <span className="ayuda">
          Paso {paso} de 4 · porcentaje → alcance → vista previa → confirmado.{' '}
          <b>Nada se escribe hasta confirmar.</b>
        </span>
      </div>

      <AvisoDeError error={error} datoDePrueba="error-del-asistente" />

      {/* ---------------- PASO 1 · EL PORCENTAJE ---------------- */}
      {paso === PASOS.porcentaje && (
        <div className="card" style={{ margin: 12 }} data-prueba="paso-porcentaje">
          <div className="fila">
            <span className="crece">Porcentaje vigente de {anio}</span>
            <b data-prueba="porcentaje-vigente-real">
              {estaFijado ? porcentaje(vigente.porcentajeIncremento) : `Sin porcentaje para ${anio}`}
            </b>
          </div>

          {/* Fila 20 del inventario del incremento 15 (RF-223): salió la jerga de API
              («la petición viaja sin él») y la palabra «fila» en sentido de base de datos. */}
          {estaFijado && (
            <span className="ayuda" data-prueba="omitir-no-borra">
              <b>Omitir el porcentaje NO lo borra.</b> Si no lo tocas, se mantiene el vigente
              ({porcentaje(vigente.porcentajeIncremento)}) y la cifra de {anio} no cambia. Para
              generar sin incremento hay que teclear <b>0</b>.
            </span>
          )}

          <label className="fila" data-prueba="opcion-mantener">
            <input
              type="radio"
              name="modo"
              checked={modo === MODOS.mantener}
              onChange={() => { setModo(MODOS.mantener); setPorcentajeTecleado(''); setConfirmadoAlcanceAnual(false); }}
            />
            <span className="crece">
              {/* RN-216 bis d: con porcentaje fijado se rotula CON SU NÚMERO REAL;
                  «dejar el año anterior tal cual» solo cabe si nadie lo ha fijado. */}
              <b>
                {estaFijado
                  ? `Mantener el ${porcentaje(vigente.porcentajeIncremento)} ya fijado para ${anio}`
                  : 'Dejar el año anterior tal cual'}
              </b>
              <small style={{ display: 'block' }}>
                {estaFijado
                  ? 'No se cambia nada del porcentaje: la fila del año sigue como está, con su autor y su fecha.'
                  : `Cada mes se sugiere con el gasto del mismo mes del año anterior, sin incremento. No se fija ningún porcentaje y no se crea ninguna fila: en cada línea queda estampado 0.00.`}
              </small>
            </span>
          </label>

          <label className="fila" data-prueba="opcion-cambiar">
            <input
              type="radio"
              name="modo"
              checked={modo === MODOS.cambiar}
              onChange={() => setModo(MODOS.cambiar)}
            />
            <span className="crece">
              <b>{estaFijado ? `Cambiar el porcentaje de ${anio}` : 'Incrementar (o reducir) en un porcentaje'}</b>
              <small style={{ display: 'block' }}>
                Un solo número para todo el año, al alza o a la baja, de −100 % a 999.99 %. Se
                aplica <b>solo</b> a las categorías con gasto del mismo mes del año anterior; al
                promedio no lo toca.
              </small>
            </span>
          </label>

          {modo === MODOS.cambiar && (
            <div className="campo">
              <label htmlFor="porcentajeDelAnio">PORCENTAJE DE {anio}</label>
              <input
                id="porcentajeDelAnio"
                inputMode="decimal"
                value={porcentajeTecleado}
                placeholder="por ejemplo 10 · o -10 para bajar"
                onChange={(evento) => setPorcentajeTecleado(evento.target.value)}
              />
              <label className="fila">
                <input
                  type="checkbox"
                  checked={confirmadoAlcanceAnual}
                  onChange={(evento) => setConfirmadoAlcanceAnual(evento.target.checked)}
                  data-prueba="confirmar-alcance-anual"
                />
                <span className="crece">
                  <b>Aplica al presupuesto de todos los meses de {anio}</b> — no es por categoría ni
                  por mes. Lo entiendo y quiero fijarlo.
                </span>
              </label>
            </div>
          )}

          <button
            className="btn ancho"
            type="button"
            disabled={!modo || trabajando}
            onClick={pedirVistaPrevia}
            data-prueba="continuar-desde-porcentaje"
          >
            Continuar
          </button>
          <span className="ayuda">
            Cambiar el porcentaje <b>no reescribe ninguna línea ya guardada</b>. Y esto no aparece
            solo: llegaste aquí porque pulsaste «Definir el presupuesto de {anio}».
          </span>
        </div>
      )}

      {/* ---------------- PASO 2 · EL ALCANCE ---------------- */}
      {paso === PASOS.alcance && previa?.comparativoDeAlcances && (
        <div className="card" style={{ margin: 12 }} data-prueba="paso-alcance">
          <b>{anio} ya tiene presupuesto. ¿Qué hacemos con lo que hay?</b>
          <span className="ayuda">
            Hay dos cosas distintas: lo que salió de una generación anterior <b>y nadie tocó</b>, y
            lo que <b>decidiste tú</b>. Elige qué pasa con lo segundo.{' '}
            <b>No hay opción marcada por omisión.</b>
          </span>

          <OpcionDeAlcance
            marca="alcance-conservar"
            elegida={alcance === ALCANCES.conservarEditadoAMano}
            alElegir={() => { setAlcance(ALCANCES.conservarEditadoAMano); setConfirmadoReemplazo(false); }}
            titulo="Conservar lo editado a mano"
            descripcion={`Se sustituye solo lo que salió de una generación anterior que nadie tocó (${previa.comparativoDeAlcances.conservarEditadoAMano.lineasPorReemplazar} líneas). Tus cifras se quedan como están.`}
            proyeccion={previa.comparativoDeAlcances.conservarEditadoAMano}
          />

          <OpcionDeAlcance
            marca="alcance-sustituir"
            destructiva
            elegida={alcance === ALCANCES.sustituirTodo}
            alElegir={() => { setAlcance(ALCANCES.sustituirTodo); setConfirmadoReemplazo(false); }}
            titulo="Sustituir todo"
            descripcion={`Se sustituyen también tus ${previa.cantidadLineasManualesEnRiesgo} cifras, las de ${listaDeMeses(previa.mesesConLineasManualesEnRiesgo)}. Es la única acción destructiva de este módulo: pedirá una confirmación aparte y te dirá antes, una por una, qué cifras se pierden.`}
            proyeccion={previa.comparativoDeAlcances.sustituirTodo}
          />

          <div className="fila">
            <button
              className="btn"
              type="button"
              onClick={() => setPaso(PASOS.vistaPrevia)}
              disabled={!alcance}
              data-prueba="ver-vista-previa"
            >
              Ver la vista previa
            </button>
            <button className="btn sec" type="button" onClick={() => setPaso(PASOS.porcentaje)}>Atrás</button>
          </div>

          {!alcance && (
            <span className="ayuda" data-prueba="alcance-sin-elegir">
              Elige un alcance. <b>No hay opción por omisión</b>, ni siquiera implícita: ni
              «conservar» ni «sustituir» es lo que pasa si no eliges. El alcance viaja como una de
              dos palabras, nunca como un sí/no.
            </span>
          )}
        </div>
      )}

      {/* ---------------- PASO 3 · LA VISTA PREVIA ---------------- */}
      {paso === PASOS.vistaPrevia && previa && (
        <>
          <div className="card" style={{ margin: 12 }} data-prueba="vista-previa">
            <b>Esto es lo que va a pasar</b>
            <span className="ayuda">
              <b>Todavía no se ha escrito nada.</b> Las cifras de abajo son exactamente las que se
              guardarán al confirmar. Porcentaje que se aplicará:{' '}
              <b data-prueba="porcentaje-aplicado">{porcentaje(previa.porcentajeAplicado)}</b>.
            </span>

            {esAnioVacio(previa) && (
              <span className="ayuda" data-prueba="anio-vacio">
                {/* Fila 21 del inventario del incremento 15 (RF-222/RF-223): salió el código
                    de catálogo CONSERVAR_EDITADO_A_MANO y la justificación de la
                    implementación («porque el parámetro es obligatorio siempre»). */}
                {anio} está vacío, así que no hay alcance que elegir: todo lo de abajo son altas.
              </span>
            )}

            {/* EL TITULAR LO MANDA EL SERVIDOR, EN CATEGORÍAS DISTINTAS Y POR
                ALCANCE (D-167). La pantalla no suma listas. */}
            <div className="fila" data-prueba="titular-sin-presupuesto">
              <span className="crece">Con el alcance elegido</span>
              <b>{avisoDeCategoriasSinPresupuesto(rama?.cantidadDeCategoriasSinPresupuesto)}</b>
            </div>

            <div className="fila">
              <span className="crece">Líneas por crear</span>
              <b data-prueba="lineas-por-crear">{rama?.lineasPorCrear ?? previa.lineasPorCrear}</b>
            </div>
            <div className="fila">
              <span className="crece">Líneas por sustituir</span>
              <b data-prueba="lineas-por-reemplazar">{rama?.lineasPorReemplazar ?? previa.lineasPorReemplazar}</b>
            </div>
            <div className="fila">
              <span className="crece">Líneas conservadas</span>
              <b data-prueba="lineas-conservadas">{rama?.lineasConservadas?.length ?? 0}</b>
            </div>
            <div className="fila">
              <span className="crece">Total presupuestado de {anio}</span>
              <b data-prueba="total-del-anio">{importe(rama?.totalPresupuestadoDelAnio)}</b>
            </div>
          </div>

          {/* Lo que queda sin cifra PASE LO QUE PASE: se lista por nombre para que
              su ausencia se vea ahora, no se descubra en diciembre (RN-229d). */}
          <div className="card lista" style={{ margin: 12 }} data-prueba="categorias-sin-cifra">
            <div className="gtit">Categorías que quedarán sin cifra</div>
            {previa.categoriasSinCifra.length === 0 ? (
              <span className="ayuda">Todas las categorías activas tendrán cifra en los doce meses.</span>
            ) : previa.categoriasSinCifra.map((categoria) => (
              <div className="fila" key={categoria.idCategoria}>
                <span className="crece">
                  <b>{categoria.nombreCategoria}</b>
                  <small style={{ display: 'block' }}>{listaDeMeses(categoria.mesesSinCifra)}</small>
                </span>
              </div>
            ))}
            {/* Fila 22 del inventario del incremento 15 (RF-222): salió la justificación del
                diseño («no depende del alcance y por eso…»). */}
            <span className="ayuda" data-prueba="categorias-sin-cifra-ayuda">
              Los meses donde ya hay una cifra tuya no van aquí: están en el bloque de abajo, con
              nombre, mes, importe y origen.
            </span>
          </div>

          {/* Sustituir sin enseñar antes qué se sustituye sería pisar en silencio.
              Una línea que se pierde NUNCA sale vacía: se rotula (RN-233 b bis). */}
          <div className="card lista" style={{ margin: 12 }} data-prueba="detalle-de-reemplazos">
            <div className="gtit">Detalle de lo que se sustituye</div>
            {previa.detalleDeReemplazos.length === 0 ? (
              <span className="ayuda">No hay ninguna línea que sustituir: todo lo del año son altas.</span>
            ) : previa.detalleDeReemplazos.map((reemplazo) => (
              <div className="fila" key={`${reemplazo.idPeriodo}-${reemplazo.idCategoria}`}>
                <span className="crece">
                  <b>{reemplazo.nombreCategoria}</b> · {nombreDePeriodo(reemplazo.idPeriodo)}
                  <small style={{ display: 'block' }}>
                    {importe(reemplazo.importeAnterior)} · {reemplazo.origenAnterior}
                  </small>
                </span>
                <span className="imp">
                  {reemplazo.importeNuevo === null || reemplazo.importeNuevo === undefined ? (
                    <b data-prueba="cifra-que-se-pierde">esta cifra se pierde · quedará sin presupuesto</b>
                  ) : importe(reemplazo.importeNuevo)}
                </span>
              </div>
            ))}
          </div>

          {/* BLOQUE DESTRUCTIVO · solo con SUSTITUIR_TODO y solo si hay algo que
              perder: una confirmación que siempre aparece deja de leerse (RN-233e). */}
          {esDestructivo && (
            <div className="aviso e" style={{ margin: 12 }} data-prueba="bloque-destructivo">
              <b>⚠ Vas a perder cifras que capturaste a mano</b>
              <span className="ayuda">
                Se sustituirán {previa.cantidadLineasManualesEnRiesgo} cifra
                {previa.cantidadLineasManualesEnRiesgo === 1 ? '' : 's'} que decidiste tú, en{' '}
                {listaDeMeses(previa.mesesConLineasManualesEnRiesgo)}.
              </span>

              {manualesEnRiesgo.map((linea) => (
                <div className="fila" key={`${linea.idPeriodo}-${linea.idCategoria}`} data-prueba="manual-en-riesgo">
                  <span className="crece">
                    <b>{linea.nombreCategoria}</b> · {nombreDePeriodo(linea.idPeriodo)}
                    <small style={{ display: 'block' }}>
                      {importe(linea.importeActual)} · {linea.origenActual}
                    </small>
                  </span>
                </div>
              ))}

              <span className="ayuda">
                <b>Queda rastro y es recuperable:</b> de cada línea sustituida se guarda su importe
                anterior, su origen anterior y con qué alcance se regeneró. La operación es
                destructiva para el dato de hoy, <b>no para su historia</b>.
              </span>

              <label className="fila">
                <input
                  type="checkbox"
                  checked={confirmadoReemplazo}
                  onChange={(evento) => setConfirmadoReemplazo(evento.target.checked)}
                  data-prueba="confirmar-reemplazo-manual"
                />
                <span className="crece">
                  Confirmo que se reemplacen esas {previa.cantidadLineasManualesEnRiesgo} cifras de{' '}
                  {listaDeMeses(previa.mesesConLineasManualesEnRiesgo)}.
                </span>
              </label>
            </div>
          )}

          <div style={{ margin: 12 }}>
            <button
              className="btn ancho"
              type="button"
              disabled={trabajando}
              onClick={confirmarYGenerar}
              data-prueba="confirmar-generacion"
            >
              {esDestructivo ? `Sustituir todo y generar ${anio}` : 'Confirmar y generar los doce meses'}
            </button>
            <button className="btn sec ancho" type="button"
              onClick={() => setPaso(esAnioVacio(previa) ? PASOS.porcentaje : PASOS.alcance)}>
              Atrás
            </button>
            {/* Fila 35 del inventario (D-335): «una sola transacción» es vocabulario de
                implementación (RN-627e). El hecho de fondo —todo o nada— le importa a la
                persona antes de confirmar y se conserva en lenguaje de producto (RN-629);
                la segunda frase ya era conforme y queda intacta. */}
            <span className="ayuda" data-prueba="generacion-todo-o-nada">
              La generación es <b>todo o nada</b>: o quedan guardados los doce meses, o no se
              guarda ninguno. Después, cualquier mes se puede editar cuando quieras.
            </span>
          </div>
        </>
      )}

      {/* ---------------- PASO 4 · LO QUE SE ESCRIBIÓ ---------------- */}
      {paso === PASOS.hecho && resultado && (
        <div className="card" style={{ margin: 12 }} data-prueba="resultado-de-la-generacion">
          <b>✓ Presupuesto de {anio} generado</b>
          <span className="ayuda">
            Los doce meses quedaron definidos en una sola operación, sin confirmar categoría por
            categoría.
          </span>

          <div className="fila"><span className="crece">Líneas creadas</span><b>{resultado.lineasPorCrear}</b></div>
          <div className="fila"><span className="crece">Líneas sustituidas</span><b>{resultado.lineasPorReemplazar}</b></div>
          <div className="fila"><span className="crece">Líneas conservadas</span><b>{resultado.lineasConservadas.length}</b></div>
          <div className="fila" data-prueba="resultado-sin-presupuesto">
            <span className="crece">Con el alcance aplicado</span>
            <b>{avisoDeCategoriasSinPresupuesto(resultado.cantidadDeCategoriasSinPresupuesto)}</b>
          </div>
          <div className="fila">
            <span className="crece">Porcentaje estampado en cada línea de base año anterior</span>
            <b>{porcentaje(resultado.porcentajeAplicado)}</b>
          </div>

          <span className="ayuda">
            Alcance aplicado: <b>{alcance}</b>. A partir de aquí todo sigue editable: entra a
            cualquier mes y cambia lo que quieras. Al guardar, el sistema preguntará <b>una sola
            vez</b> si ese cambio debe seguir a los meses siguientes de {anio}.
          </span>

          <Link className="btn ancho" to={`/presupuesto/registro/${periodoDeOrigen ?? `${anio}01`}`}>
            Ver el presupuesto de un mes
          </Link>
          <Link className="btn sec ancho" to="/presupuesto">Ir al tablero</Link>
        </div>
      )}
    </Shell>
  );
}

/**
 * AÑO VACÍO (D-160, RN-233a bis): sin líneas por reemplazar ni conservadas en
 * NINGUNA de las dos ramas. Se lee de la vista previa, sin ningún campo nuevo.
 */
function esAnioVacio(previa) {
  const comparativo = previa?.comparativoDeAlcances;
  if (!comparativo) return false;
  return [comparativo.conservarEditadoAMano, comparativo.sustituirTodo].every(
    (proyeccion) => proyeccion.lineasPorReemplazar === 0 && (proyeccion.lineasConservadas ?? []).length === 0,
  );
}

/** Una de las DOS opciones de alcance. Nombrada, con su cifra y sin preselección. */
function OpcionDeAlcance({ marca, titulo, descripcion, proyeccion, elegida, alElegir, destructiva = false }) {
  return (
    <label className={`fila${destructiva ? ' aviso e' : ''}`} data-prueba={marca}>
      <input type="radio" name="alcanceDeRegeneracion" checked={elegida} onChange={alElegir} />
      <span className="crece">
        <b>{titulo}</b>
        <small style={{ display: 'block' }}>{descripcion}</small>
        <small style={{ display: 'block' }} data-prueba={`${marca}-sin-presupuesto`}>
          {/* Número del servidor, por alcance y en categorías distintas (D-167). */}
          {avisoDeCategoriasSinPresupuesto(proyeccion.cantidadDeCategoriasSinPresupuesto)}
        </small>
        <small style={{ display: 'block' }}>
          Total del año con este alcance: {importe(proyeccion.totalPresupuestadoDelAnio)}
        </small>
      </span>
    </label>
  );
}
